import React, { useEffect, useMemo, useRef, useState } from 'react'
import useTranslation from '~/components/shared/TranslationProvider'
import { Button } from '~/components/_atoms'
import { HandThumbUpIcon } from '@heroicons/react/24/outline'
import { Textarea } from '~/lib/ui/textarea'
import { apiPatch } from '~/util/api'
import { cn } from '../../../lib/utils'
import StarRating from '../ResumeReview/CandidateFeedback/StarRating'

export const FeedbackCarousel = ({
  serviceRequestId,
  serviceRequestName,
  serviceRequestType,
  className,
}) => {
  const STEP_FADE_OUT_MS = 250
  const STEP_FADE_IN_MS = 250

  const { t } = useTranslation('FeedbackCarousel')
  const [step, setStep] = useState(1)
  const [ratings, setRatings] = useState([0, 0])
  const [isStepFadingOut, setIsStepFadingOut] = useState(false)
  const [isTransitioningStep, setIsTransitioningStep] = useState(false)
  const [feedback, setFeedback] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [fadeOutSuccess, setFadeOutSuccess] = useState(false)
  const [hideSuccess, setHideSuccess] = useState(false)
  const stepFadeOutTimerRef = useRef(null)
  const stepFadeInTimerRef = useRef(null)
  const leftAligned = className?.includes('text-left')

  const stepConfig = useMemo(() => {
    return [
      {
        title: t('question_steps.0.title', { service: serviceRequestName }),
        helper: t('question_steps.0.helper'),
      },
      {
        title: t('question_steps.1.title'),
        helper: t('question_steps.1.helper'),
      },
    ]
  }, [serviceRequestName, t])

  const setRatingForCurrentStep = (rating) => {
    if (rating === 0 || isTransitioningStep) return

    const idx = step - 1
    const updated = [...ratings]
    updated[idx] = rating
    setRatings(updated)

    if (step < 3) {
      clearTimeout(stepFadeOutTimerRef.current)
      clearTimeout(stepFadeInTimerRef.current)

      setIsTransitioningStep(true)
      setIsStepFadingOut(true)

      stepFadeOutTimerRef.current = setTimeout(() => {
        setStep((prev) => prev + 1)
        setIsStepFadingOut(false)

        stepFadeInTimerRef.current = setTimeout(() => {
          setIsTransitioningStep(false)
        }, STEP_FADE_IN_MS)
      }, STEP_FADE_OUT_MS)
    }
  }

  const handleSubmit = async () => {
    try {
      setSubmitting(true)
      const formData = new FormData()
      formData.append(
        'feedback_service_request[service_request_id]',
        serviceRequestId,
      )
      formData.append(
        'feedback_service_request[service_request_type]',
        serviceRequestType,
      )
      formData.append('feedback_service_request[rating]', ratings[0])
      formData.append('feedback_service_request[coach_rating]', ratings[1])
      formData.append('feedback_service_request[feedback]', feedback)
      formData.append('feedback_service_request[more_prepared]', '')

      await apiPatch('/coach_services/service_request_rating', formData)
      setFadeOutSuccess(false)
      setHideSuccess(false)
      setSubmitted(true)
    } catch (error) {
      console.error('Failed to submit carousel feedback:', error)
    } finally {
      setSubmitting(false)
    }
  }

  useEffect(() => {
    if (!submitted) return undefined

    const fadeTimer = setTimeout(() => setFadeOutSuccess(true), 1200)
    const hideTimer = setTimeout(() => setHideSuccess(true), 2000)

    return () => {
      clearTimeout(fadeTimer)
      clearTimeout(hideTimer)
    }
  }, [submitted])

  useEffect(() => {
    return () => {
      clearTimeout(stepFadeOutTimerRef.current)
      clearTimeout(stepFadeInTimerRef.current)
    }
  }, [])

  if (submitted) {
    if (hideSuccess) return null

    return (
      <div
        className={
          'max-w-[640px] overflow-hidden rounded-[6px] bg-[linear-gradient(30deg,#1C6F29_0%,#75B21D_100%)] p-[2px] transition-opacity duration-700' +
          (fadeOutSuccess ? ' opacity-0' : ' opacity-100')
        }
      >
        <div className="h-4 rounded-t-[6px] bg-[linear-gradient(90deg,var(--Secondary-coach-light-green,_#75B21D)_0%,var(--Primary-default,_#08467C)_50%,var(--Secondary-coach-green,_#1C6F29)_100%)]"></div>
        <div className="flex min-h-[270px] flex-col justify-evenly overflow-hidden rounded-[4px] bg-white py-4 text-center font-roboto">
          <div className="mx-auto mb-1 flex h-14 w-14 items-center justify-center rounded-full bg-[#E9F2E2]">
            <HandThumbUpIcon
              className="h-10 w-10 text-[#1C6F29]"
              aria-hidden="true"
            />
          </div>
          <div className="text-base font-medium leading-normal tracking-[0.016px] text-[var(--Neutral-default,#2F2F2F)]">
            {t('thanks_for_sharing')}
          </div>
          <p className="text-sm text-neutral-600">{stepConfig[0].helper}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-[640px] overflow-hidden rounded-[6px] bg-[linear-gradient(30deg,#1C6F29_0%,#75B21D_100%)] p-[2px]">
      <div className="h-4 rounded-t-[6px] bg-[linear-gradient(90deg,var(--Secondary-coach-light-green,_#75B21D)_0%,var(--Primary-default,_#08467C)_50%,var(--Secondary-coach-green,_#1C6F29)_100%)]"></div>
      <div
        className={cn(
          'flex min-h-[270px] flex-col justify-between overflow-hidden rounded-[4px] bg-white py-4 text-center font-roboto',
          className,
        )}
      >
        <div
          className={cn(
            'flex flex-1 flex-col transition-opacity',
            isStepFadingOut
              ? 'duration-250 opacity-0'
              : 'duration-250 opacity-100',
            isTransitioningStep && 'pointer-events-none',
          )}
        >
          {step < 3 ? (
            <div className="flex flex-1 flex-col justify-evenly px-4 py-4">
              <div className="text-base font-medium leading-normal tracking-[0.016px] text-[var(--Neutral-default,#2F2F2F)]">
                {stepConfig[step - 1].title}
              </div>
              <p className="text-sm text-neutral-600">
                {stepConfig[step - 1].helper}
              </p>
              <div
                className={cn(
                  'flex items-center',
                  leftAligned ? 'justify-start' : 'justify-center',
                )}
                aria-label={`Step ${step} star rating`}
              >
                <StarRating
                  value={ratings[step - 1]}
                  onChange={setRatingForCurrentStep}
                  idPrefix={`feedback-step-${step}`}
                />
              </div>
            </div>
          ) : (
            <div className="flex-1 space-y-2 px-4">
              <div className="text-base font-medium leading-normal tracking-[0.016px] text-[var(--Neutral-default,#2F2F2F)]">
                {t('question_steps.2.title', { service: serviceRequestName })}
              </div>
              <Textarea
                value={feedback}
                onChange={(event) => setFeedback(event.target.value)}
                className="min-h-32 text-left text-neutral-600"
                placeholder={t('additional_feedback')}
              />
              <div className="flex items-center justify-center">
                <Button
                  component="button"
                  size="sm"
                  disabled={submitting}
                  onClick={handleSubmit}
                >
                  {submitting ? t('submitting') : t('submit')}
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="px-4 text-xs font-medium uppercase tracking-wide text-neutral-500">
          {t('step_counter', { currentStep: step, totalSteps: 3 })}
        </div>
      </div>
    </div>
  )
}

export default FeedbackCarousel
